import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiCheckCircle, FiPackage, FiHome } from 'react-icons/fi';
import { toast } from 'react-toastify';
import API from '../services/api';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        const orderId =
          localStorage.getItem('pendingOrderId') ||
          searchParams.get('orderId');

        if (!orderId) {
          toast.error('Aucune commande trouvée');
          navigate('/');
          return;
        }

        const response = await API.post('/payment/verify', { orderId });

        if (!response.data?.success) {
          throw new Error(response.data?.message || 'Paiement non confirmé');
        }

        const data = response.data.data;

        if (data.paymentStatus !== 'completed') {
          throw new Error('Paiement non complété');
        }

        setPaymentData(data);

        // Conversion marketing : achat confirmé (Meta/GA/TikTok via helper global).
        try {
          if (window.mxTrack) window.mxTrack('Purchase', {
            value: Number(data.amount || data.total || 0) || 0,
            currency: 'MAD',
            order_id: data.orderNumber || orderId
          });
        } catch (e) {}

        // Nettoyage
        localStorage.removeItem('pendingOrderId');
        localStorage.removeItem('cart');

        toast.success('Paiement confirmé avec succès !');
      } catch (error) {
        console.error('❌ Vérification paiement échouée:', error);
        toast.error('Erreur lors de la vérification du paiement');
        setTimeout(() => navigate('/payment/failed'), 2000);
      } finally {
        setVerifying(false);
      }
    };

    verifyPayment();
  }, [searchParams, navigate]);

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin h-16 w-16 border-b-4 border-primary-600 rounded-full mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Vérification du paiement…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-xl p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6">
            <FiCheckCircle className="h-12 w-12 text-green-600" />
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Paiement Réussi
          </h2>

          <p className="text-gray-600 mb-8">
            Votre paiement a été traité avec succès.
          </p>

          {paymentData && (
            <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
              <h3 className="font-semibold mb-4">Détails de la transaction</h3>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span>Transaction ID</span>
                  <span className="font-medium">{paymentData.transactionId}</span>
                </div>

                <div className="flex justify-between">
                  <span>Montant</span>
                  <span className="font-medium">
                    {Number(paymentData.amount || 0).toFixed(2)} MAD
                  </span>
                </div>

                <div className="flex justify-between">
                  <span>Méthode</span>
                  <span className="font-medium">
                    Carte bancaire (Payzone)
                  </span>
                </div>

                <div className="flex justify-between">
                  <span>Date</span>
                  <span className="font-medium">
                    {new Date(paymentData.paidAt).toLocaleString('fr-FR')}
                  </span>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={() => navigate('/dashboard/orders')}
              className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 transition flex items-center justify-center"
            >
              <FiPackage className="mr-2" />
              Voir mes commandes
            </button>

            <button
              onClick={() => navigate('/')}
              className="w-full border border-gray-300 py-3 rounded-lg text-gray-700 hover:bg-gray-50 transition flex items-center justify-center"
            >
              <FiHome className="mr-2" />
              Retour à l’accueil
            </button>
          </div>

          <p className="mt-6 text-sm text-gray-500">
            Un email de confirmation vous a été envoyé.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
